/**
 * Hundo Raid CPs, Profile page (2026-09-04 owner ask).
 *
 * "Pick a boss, read the number." A sprite-led picker lists every Pokémon in
 * raids right now, roster tiers first and then the bosses of live and
 * imminent raid events (GO Fest's Mega Finale on the eve of the event), and
 * the block beneath shows the boss big with its 15/15/15 catch CP twice:
 * plain (level 20) and weather boosted (level 25), with the weather that
 * boosts it drawn as the game's type discs.
 *
 * The list comes from hundoRaidCatalogue (pure); the CP tiles are the raid
 * hub's RaidBossHeadline, so the two surfaces can never disagree on how a
 * hundo CP is drawn. A native <select> cannot show a sprite, hence the
 * listbox.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Cloud, CloudFog, CloudRain, CloudSun, Crosshair, Snowflake, Sun, Wind } from "lucide-react";
import { InfoBlock } from "@/components/InfoBlock";
import { CaughtSprite } from "@/components/CaughtSprite";
import { RaidBossHeadline } from "@/components/RaidBossHeadline";
import { TypeIcon } from "@/components/TypeIcon";
import { raidBossArtCandidates, raidBossSilhouetteSlug } from "@/lib/formSpriteCandidates";
import { eventsLiveKey } from "@/lib/eventsLiveKey";
import {
  buildHundoRaidCatalogue,
  defaultHundoPick,
  type HundoRaidEntry,
  type LiveEventsResponseLike,
  type RaidsResponseLike,
} from "@/lib/hundoRaidCatalogue";
import type { RaidBossRef } from "@/lib/raidHubClient";
import { boostingWeathers, WEATHER_LABELS, type WeatherKind } from "@/lib/weatherBoost";

const WEATHER_ICONS: Record<WeatherKind, typeof Sun> = {
  sunny: Sun,
  rainy: CloudRain,
  partly_cloudy: CloudSun,
  cloudy: Cloud,
  windy: Wind,
  snow: Snowflake,
  fog: CloudFog,
};

/** "Sat 5 Sep", device-local, for an event that has not started. */
function shortDay(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/**
 * The hero's boss reference. `formId` is the ART form: an event boss keeps
 * `formId` null (its price comes from the attackers label instead) and
 * carries the form it should be drawn as on `art.formId`. Passing only
 * `e.formId` drew Armored Mewtwo as plain Mewtwo (owner report 2026-09-06).
 */
export function headlineRef(e: HundoRaidEntry): RaidBossRef {
  return {
    speciesId: e.speciesId,
    name: e.name,
    formId: e.formId ?? e.art.formId ?? null,
    tier: e.tier === "event" ? (e.isMega ? "mega" : "5") : e.tier,
    canBeShiny: e.canBeShiny,
    hundoCp: e.hundoCp,
  };
}

export function HundoRaidCpBlock() {
  const raidsQ = useQuery<RaidsResponseLike>({
    queryKey: ["/api/events/raids"],
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
  });
  const liveQ = useQuery<LiveEventsResponseLike>({
    queryKey: [eventsLiveKey()],
    staleTime: 5 * 60_000,
  });
  const entries = useMemo(
    () => buildHundoRaidCatalogue(raidsQ.data, liveQ.data),
    [raidsQ.data, liveQ.data],
  );
  const [pickKey, setPickKey] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Close on an outside tap; the list sits inline, not in a portal.
  useEffect(() => {
    if (!open) return;
    const onDown = (ev: PointerEvent) => {
      if (listRef.current && !listRef.current.contains(ev.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open]);

  const picked = entries.find((e) => e.key === pickKey) ?? defaultHundoPick(entries);
  const loading = raidsQ.isLoading || liveQ.isLoading;
  if (!loading && entries.length === 0) return null;

  // Group headings in first-seen order (roster tiers, then each event).
  const groups: Array<{ group: string; items: HundoRaidEntry[] }> = [];
  for (const e of entries) {
    const g = groups.find((x) => x.group === e.group);
    if (g) g.items.push(e);
    else groups.push({ group: e.group, items: [e] });
  }
  const weathers = picked ? boostingWeathers(picked.types) : [];
  const startsOn = picked && picked.tier === "event" ? shortDay(picked.startsAt) : null;

  return (
    <InfoBlock icon={Crosshair} title="Hundo Raid CPs" data-testid="profile-hundo-raid-cps">
      <div ref={listRef} className="relative" data-testid="hundo-raid-picker">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={!picked}
          data-testid="hundo-raid-picker-trigger"
          className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left"
          style={{
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            minHeight: 48,
          }}
        >
          {picked ? (
            <>
              <CaughtSprite
                id={picked.speciesId}
                size={32}
                shiny={false}
                ariaLabel=""
                formSpriteCandidates={raidBossArtCandidates(picked.art)}
                formSlug={raidBossSilhouetteSlug(picked.art)}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                  {picked.name}
                </span>
                <span className="block text-[9px] font-bold uppercase tracking-[0.14em] truncate" style={{ color: "var(--text-tertiary)" }}>
                  {picked.group}
                </span>
              </span>
            </>
          ) : (
            <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              Loading the raid roster…
            </span>
          )}
          <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />
        </button>

        {open && (
          <div
            role="listbox"
            aria-label="Raid bosses"
            data-testid="hundo-raid-picker-list"
            className="absolute left-0 right-0 z-20 mt-1 rounded-xl overflow-y-auto"
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--border-subtle)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              maxHeight: 320,
            }}
          >
            {groups.map(({ group, items }) => (
              <div key={group} role="group" aria-label={group}>
                <p
                  className="sticky top-0 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-[0.16em]"
                  style={{ background: "var(--surface-raised)", color: "var(--text-tertiary)" }}
                >
                  {group}
                </p>
                {items.map((e) => {
                  const selected = picked?.key === e.key;
                  return (
                    <button
                      key={e.key}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-testid={`hundo-raid-option-${e.key}`}
                      onClick={() => {
                        setPickKey(e.key);
                        setOpen(false);
                      }}
                      className="w-full flex items-center gap-2.5 px-2.5 py-1.5 text-left"
                      style={{
                        background: selected ? "hsl(var(--accent) / 0.12)" : "transparent",
                        minHeight: 40,
                      }}
                    >
                      <CaughtSprite
                        id={e.speciesId}
                        size={28}
                        shiny={false}
                        ariaLabel=""
                        formSpriteCandidates={raidBossArtCandidates(e.art)}
                        formSlug={raidBossSilhouetteSlug(e.art)}
                      />
                      <span className="min-w-0 flex-1 text-[12px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                        {e.name}
                      </span>
                      {e.hundoCp && (
                        <span className="text-[11px] font-bold tabular-nums shrink-0" style={{ color: "var(--text-secondary)" }}>
                          {e.hundoCp.normal.toLocaleString()}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {picked && (
        <div className="mt-3" data-testid="hundo-raid-pick" data-key={picked.key}>
          <RaidBossHeadline boss={headlineRef(picked)} size={88} />
          {!picked.hundoCp && (
            <p className="mt-2 text-[11px] text-center" style={{ color: "var(--text-tertiary)" }} data-testid="hundo-raid-no-cp">
              No base stats for this form yet, so no CP to show.
            </p>
          )}
          {weathers.length > 0 && picked.hundoCp && (
            <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5" data-testid="hundo-raid-weather">
              {weathers.map(({ weather, types }) => {
                const Icon = WEATHER_ICONS[weather];
                return (
                  <span
                    key={weather}
                    className="inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-0.5 text-[10px] font-semibold"
                    style={{
                      background: "hsl(207 90% 60% / 0.10)",
                      border: "1px solid hsl(207 90% 60% / 0.35)",
                      color: "hsl(207 90% 60%)",
                    }}
                    data-testid={`hundo-raid-weather-${weather}`}
                  >
                    <Icon size={12} aria-hidden />
                    {WEATHER_LABELS[weather]}
                    <span className="inline-flex items-center gap-0.5 ml-0.5">
                      {types.map((t) => (
                        <TypeIcon key={t} type={t} size={12} />
                      ))}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
          <p className="mt-2 text-[9.5px] text-center" style={{ color: "var(--text-tertiary)" }} data-testid="hundo-raid-source">
            {startsOn ? `${picked.group}, from ${startsOn}. ` : ""}
            {picked.cpSource === "leekduck"
              ? "CPs from the raid feed."
              : picked.cpSource === "computed"
                ? "CPs computed from base stats."
                : ""}
          </p>
        </div>
      )}
    </InfoBlock>
  );
}
